import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { ReflectionParamTypeEnum } from './enums/reflection-param-type.enum';
import { ReflectionRecipientTypeEnum } from './enums/reflection-recipient-type.enum';
import { ReflectionParam } from './reflections.models';

export interface ReflectionConfigPayload {
  config: Record<string, unknown>;
  secrets: Record<string, string | null>;
}

interface RecipientEntry {
  type?: ReflectionRecipientTypeEnum | null;
  user_ids?: number[] | null;
  field_key?: string | null;
}

const isBlank = (value: unknown) =>
  value === null || value === undefined || value === '';

/** Selected ids of a user lookup (a plain array, or the nulls of unselected rows). */
const idsOf = (value: unknown): number[] =>
  Array.isArray(value) ? value.filter(id => !isBlank(id)) : [];

/** Whether the param is shown for the given (raw) config value. Hidden params are not validated or sent. */
export function isParamVisible(
  param: ReflectionParam,
  value: Record<string, unknown>
): boolean {
  const rule = param.visible_when;
  return !rule || value[rule.param] === rule.equals;
}

/** A recipient needs its users (`users`) or its field (`user_field`); the other types carry nothing. */
const recipientRowValidator: ValidatorFn = (
  row: AbstractControl
): ValidationErrors | null => {
  const { type, user_ids, field_key } = row.getRawValue();
  if (type === ReflectionRecipientTypeEnum.USERS && !idsOf(user_ids).length)
    return { userIdsRequired: true };
  if (type === ReflectionRecipientTypeEnum.USER_FIELD && isBlank(field_key))
    return { fieldKeyRequired: true };
  return null;
};

export function buildRecipientRow(entry: RecipientEntry = {}): FormGroup {
  return new FormGroup(
    {
      type: new FormControl(entry.type ?? null, Validators.required),
      user_ids: new FormControl<number[]>(idsOf(entry.user_ids)),
      field_key: new FormControl<string | null>(entry.field_key ?? null),
    },
    recipientRowValidator
  );
}

const textParam = (key: string): ReflectionParam => ({
  key,
  type: ReflectionParamTypeEnum.TEXT,
  label: { en: '', ar: '' },
  required: false,
  default: null,
  hint: null,
  visible_when: null,
  allowed_field_types: [],
  options: [],
  min: null,
  max: null,
  placeholders: false,
  key_param: null,
  value_param: null,
});

/** One `{key, value}` entry of a `map` param; each side is built from its own param. */
export function buildMapRow(
  param: ReflectionParam,
  entry: { key?: unknown; value?: unknown } = {}
): FormGroup {
  return new FormGroup({
    key: buildParamControl(param.key_param ?? textParam('key'), entry.key),
    value: buildParamControl(
      param.value_param ?? textParam('value'),
      entry.value
    ),
  });
}

function buildParamControl(
  param: ReflectionParam,
  raw: unknown
): AbstractControl {
  const value = raw ?? param.default ?? null;
  const required = param.required ? [Validators.required] : [];
  const rows = Array.isArray(value) ? value : [];

  switch (param.type) {
    case ReflectionParamTypeEnum.MAP:
      return new FormArray(
        rows.map(entry => buildMapRow(param, entry)),
        required
      );
    case ReflectionParamTypeEnum.RECIPIENTS:
      return new FormArray(
        rows.map(entry => buildRecipientRow(entry)),
        required
      );
    case ReflectionParamTypeEnum.LOCALIZED_TEXT: {
      const text = (value ?? {}) as { en?: string; ar?: string };
      return new FormGroup({
        en: new FormControl(text.en ?? '', required),
        ar: new FormControl(text.ar ?? '', required),
      });
    }
    case ReflectionParamTypeEnum.FIELD_KEY_LIST:
    case ReflectionParamTypeEnum.MULTI_ENUM:
    case ReflectionParamTypeEnum.USER_LIST:
      return new FormControl<unknown[]>([...rows], required);
    case ReflectionParamTypeEnum.BOOL:
      return new FormControl<boolean>(value === true);
    case ReflectionParamTypeEnum.NUMBER: {
      const limits = [
        ...(param.min !== null ? [Validators.min(param.min)] : []),
        ...(param.max !== null ? [Validators.max(param.max)] : []),
      ];
      return new FormControl<number | null>(value as number | null, [
        ...required,
        ...limits,
      ]);
    }
    case ReflectionParamTypeEnum.SECRET:
      // Write-only: the stored value is never returned, so "required" cannot be enforced here.
      return new FormControl<string>('');
    case ReflectionParamTypeEnum.TEXT:
    case ReflectionParamTypeEnum.URL:
      return new FormControl<string>((value as string) ?? '', required);
    default:
      return new FormControl<unknown>(value, required);
  }
}

/** Hidden params are disabled, so they are skipped by `value` and by validation. */
function syncVisibility(schema: ReflectionParam[], group: FormGroup): void {
  const raw = group.getRawValue();
  for (const param of schema) {
    if (!param.visible_when) continue;
    const control = group.controls[param.key];
    const visible = isParamVisible(param, raw);
    if (visible && control.disabled) control.enable({ emitEvent: false });
    else if (!visible && control.enabled) control.disable({ emitEvent: false });
  }
}

/** One control per param, seeded from `config` (or the param default). */
export function buildConfigGroup(
  schema: ReflectionParam[],
  config: Record<string, unknown>
): FormGroup {
  const group = new FormGroup(
    Object.fromEntries(
      schema.map(param => [
        param.key,
        buildParamControl(param, config[param.key]),
      ])
    )
  );
  syncVisibility(schema, group);
  group.valueChanges.subscribe(() => syncVisibility(schema, group));
  return group;
}

function payloadValue(param: ReflectionParam, value: unknown): unknown {
  switch (param.type) {
    case ReflectionParamTypeEnum.NUMBER:
      return isBlank(value) ? undefined : Number(value);
    case ReflectionParamTypeEnum.USER_LIST:
      return idsOf(value);
    case ReflectionParamTypeEnum.MAP:
      return ((value as { key: unknown; value: unknown }[]) ?? [])
        .filter(entry => !isBlank(entry.key))
        .map(entry => ({ key: entry.key, value: entry.value }));
    case ReflectionParamTypeEnum.RECIPIENTS:
      return ((value as RecipientEntry[]) ?? []).map(row => {
        switch (row.type) {
          case ReflectionRecipientTypeEnum.USERS:
            return { type: row.type, user_ids: idsOf(row.user_ids) };
          case ReflectionRecipientTypeEnum.USER_FIELD:
            return { type: row.type, field_key: row.field_key };
          default:
            return { type: row.type };
        }
      });
    default:
      return isBlank(value) ? undefined : value;
  }
}

/** Splits the form into the `config` object and the write-only `secrets` (an empty secret is omitted). */
export function toConfigPayload(
  schema: ReflectionParam[],
  group: FormGroup
): ReflectionConfigPayload {
  const raw = group.getRawValue();
  const config: Record<string, unknown> = {};
  const secrets: Record<string, string | null> = {};

  for (const param of schema) {
    if (!isParamVisible(param, raw)) continue;
    const value = raw[param.key];
    if (param.type === ReflectionParamTypeEnum.SECRET) {
      if (typeof value === 'string' && value !== '') secrets[param.key] = value;
      continue;
    }
    const normalized = payloadValue(param, value);
    if (normalized !== undefined) config[param.key] = normalized;
  }
  return { config, secrets };
}
